import axios from 'axios';
import { useAuthStore } from '@store/authStore';

export type ErpAuthProvider = 'quickbooks' | 'netsuite' | 'xero' | 'ajera';
export const ERP_AUTH_PROVIDERS: ErpAuthProvider[] = ['quickbooks', 'netsuite', 'xero', 'ajera'];

export const isErpAuthProvider = (provider?: string | null): provider is ErpAuthProvider =>
  ERP_AUTH_PROVIDERS.includes(provider as ErpAuthProvider);

export interface QuickBooksAuthorizationResponse {
  is_authenticated: boolean;
  realm_id?: string;
  authorization_url?: string;
}

export interface ErpConnectionCredentials {
  api_url?: string;
  api_id?: string;
  username?: string;
  password?: string;
}

interface QuickBooksAuthStatusOptions {
  resumeWorkflowId?: string;
  returnPath?: string;
  activeTenantId?: string | null;
}

interface QuickBooksReconnectOptions extends QuickBooksAuthStatusOptions {
  forceReconnect?: boolean;
  credentials?: ErpConnectionCredentials;
}

const API_URL = '/api';
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000');
const ERP_PROVIDER_LABELS: Record<string, string> = {
  quickbooks: 'QuickBooks Online',
  quickbooks_desktop: 'QuickBooks Desktop',
  netsuite: 'NetSuite',
  xero: 'Xero',
  ajera: 'Deltek Ajera',
};

const getDefaultReturnPath = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const getActiveTenantHeaders = (activeTenantId?: string | null): Record<string, string> => {
  const tenantId = activeTenantId ?? useAuthStore.getState().activeTenantId ?? useAuthStore.getState().currentUser?.active_tenant_id;
  return tenantId ? { 'X-Active-Tenant-ID': tenantId } : {};
};

export const getErpProviderLabel = (provider?: string | null): string => {
  if (!provider) {
    return 'ERP';
  }
  return ERP_PROVIDER_LABELS[provider] ?? provider
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const fetchErpAuthStatus = async (
  provider: ErpAuthProvider,
  options: QuickBooksAuthStatusOptions = {}
): Promise<QuickBooksAuthorizationResponse> => {
  const resolvedReturnPath = options.returnPath ?? getDefaultReturnPath();
  const params = new URLSearchParams();

  if (options.resumeWorkflowId) {
    params.set('resume_workflow_id', options.resumeWorkflowId);
  }

  if (resolvedReturnPath) {
    params.set('return_path', resolvedReturnPath);
  }


  const queryString = params.toString();

  const response = await axios.get<QuickBooksAuthorizationResponse>(
    `${API_URL}/auth/${provider}/status${queryString ? `?${queryString}` : ''}`,
    {
      timeout: API_TIMEOUT,
      withCredentials: true,
      headers: getActiveTenantHeaders(options.activeTenantId),
    }
  );

  return response.data;
};

export const requestErpAuthorization = async (
  provider: ErpAuthProvider,
  options: QuickBooksReconnectOptions = {}
): Promise<QuickBooksAuthorizationResponse> => {
  const resolvedReturnPath = options.returnPath ?? getDefaultReturnPath();
  const authorizationPath = provider === 'ajera'
    ? `/erp/connection/${provider}/authorize`
    : `/erp/connections/${provider}/authorize`;
  const response = await axios.post<QuickBooksAuthorizationResponse>(
    `${API_URL}${authorizationPath}`,
    {
      resume_workflow_id: options.resumeWorkflowId,
      return_path: resolvedReturnPath,
      force_reconnect: Boolean(options.forceReconnect),
      credentials: options.credentials,
    },
    {
      timeout: API_TIMEOUT,
      withCredentials: true,
      headers: getActiveTenantHeaders(),
    }
  );

  return response.data;
};

export const redirectToErpAuthorization = async (
  provider: ErpAuthProvider,
  options: QuickBooksReconnectOptions = {}
): Promise<boolean> => {
  const { authorization_url, is_authenticated } = await requestErpAuthorization(provider, options);

  if (authorization_url) {
    window.location.href = authorization_url;
    return true;
  }

  return is_authenticated;
};

export const fetchQuickBooksAuthStatus = async (
  options: QuickBooksAuthStatusOptions = {}
): Promise<QuickBooksAuthorizationResponse> => fetchErpAuthStatus('quickbooks', options);

export const requestQuickBooksAuthorization = async (
  options: QuickBooksReconnectOptions = {}
): Promise<QuickBooksAuthorizationResponse> => requestErpAuthorization('quickbooks', options);

export const redirectToQuickBooksAuthorization = async (
  options: QuickBooksReconnectOptions = {}
): Promise<boolean> => redirectToErpAuthorization('quickbooks', options);
